/** @jsxRuntime automatic */
import { type HTMLAttributes, type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { useLiveChange } from "./live-change";
import { Presence } from "./presence";
import { LazyAnimatedNumber } from "./swap-boot";
import { cn } from "./utils";

/**
 * Colors come from apps/web `globals.css` (`.ui-badge--*`) so contrast is correct
 * even when Tailwind does not scan this package.
 *
 * New code uses `tone` (semantic). `variant` (raw hue names) is the legacy API
 * kept so screens migrate one at a time; never use hues for pipeline status
 * (StatusPill) or match grades (MatchGrade).
 */
const badgeVariants = cva("ui-badge", {
  variants: {
    /** @deprecated Raw hue. Use `tone`. */
    variant: {
      default: "ui-badge--default",
      gray: "ui-badge--gray",
      blue: "ui-badge--blue",
      green: "ui-badge--green",
      yellow: "ui-badge--yellow",
      red: "ui-badge--red",
      purple: "ui-badge--purple",
      amber: "ui-badge--amber",
      emerald: "ui-badge--emerald",
      indigo: "ui-badge--indigo",
      cyan: "ui-badge--cyan",
    },
    tone: {
      /** Data the user owns (skills, cities, portals) and plain meta. */
      neutral: "ui-badge--neutral",
      /** Outline chip: quiet, for tags next to other badges. */
      outline: "ui-badge--outline",
      /** Winche's own work: "Nuevo", PRO, "Por enviar". */
      brand: "ui-badge--brand",
      info: "ui-badge--info",
      success: "ui-badge--success",
      warning: "ui-badge--warning",
      danger: "ui-badge--danger",
    },
    size: {
      /** 20 px, 11 px text: dense rows and kanban cards. */
      sm: "ui-badge--sm",
      /** 22 px, 12 px text. */
      md: "ui-badge--md",
    },
  },
});

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** 6 px dot in the tone's solid color before the label. */
  dot?: boolean;
  /** Leading icon (12–14 px). */
  icon?: ReactNode;
  /**
   * Ellipsis instead of wrapping when the badge is wider than its container.
   * Pass the full text in `title` when it can be cut.
   */
  truncate?: boolean;
}

export function Badge({
  className,
  variant,
  tone,
  size,
  dot,
  icon,
  truncate,
  children,
  ...props
}: BadgeProps) {
  // Legacy look when neither tone nor variant is given.
  const resolvedVariant = variant ?? (tone ? undefined : "default");
  const structured = dot || icon || truncate;
  return (
    <span
      className={cn(
        badgeVariants({ variant: resolvedVariant, tone, size }),
        structured && "gap-1",
        truncate && "min-w-0 max-w-full",
        className,
      )}
      {...props}
    >
      {dot ? <span className="ui-badge__dot" aria-hidden="true" /> : null}
      {icon ? (
        <span className="ui-badge__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {truncate ? <span className="min-w-0 truncate">{children}</span> : children}
    </span>
  );
}

export interface CountBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  count: number;
  /** Values above it render as `{max}+`. */
  max?: number;
  /**
   * `strong` (ink, default): news that invites action (CVs ready, replies).
   * `brand`: Winche's own news. `danger`: only for real errors or overdue items.
   */
  tone?: "strong" | "brand" | "danger";
  /**
   * What the number counts, for screen readers ("3 pendientes"). The digit is
   * then hidden from them, so a nav link reads "Postulaciones, 3 pendientes"
   * and not "3Postulaciones" (a11y-16). Put the badge after the link text.
   */
  label?: string;
}

/** Counts read as they always did: no grouping (a badge caps at `max`). */
const COUNT_FORMAT: Intl.NumberFormatOptions = { useGrouping: false };

/**
 * Counter for nav items and tabs. Good news is never red: the default is an
 * ink pill with a ring that separates it from the icon under it.
 *
 * Nothing at 0. Keep it mounted and pass the count (never `{n > 0 && …}`):
 * when it appears after the first paint it pops in (`pop-glyph`, 200), and
 * at 0 it fades out by opacity only in 130 with its last digits (spec 07 ›
 * Movimiento). A change of number never bounces it: inside a live scope its
 * changed digits roll (AnimatedNumber, which loads after the mount: the badge
 * rides in every page's start-up, and until then the number is still). The
 * pill already has the new width and clips its digits (always: its own ring
 * and the digits at rest are inside it, and a `:has([data-rolling])` toggle
 * restyled the whole page at each roll): a place it lost (10 → 9) leaves
 * inside it, never over the ring.
 */
export function CountBadge({ count, max = 99, tone = "strong", label, className, ...props }: CountBadgeProps) {
  const show = count > 0;
  const appeared = useLiveChange(show);
  const hidden = label ? "true" : undefined;
  const digits = (
    <LazyAnimatedNumber value={count} max={max} format={COUNT_FORMAT} aria-hidden={hidden} fallback={<span aria-hidden={hidden}>{count > max ? `${max}+` : count}</span>} />
  );
  return (
    <Presence show={show} latch>
      <span
        className={cn(
          "inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-2xs font-semibold leading-none tabular-nums ring-2 ring-background overflow-clip data-[state=closed]:animate-fade-out",
          appeared && "animate-pop-glyph",
          tone === "strong" && "bg-foreground text-background",
          tone === "brand" && "bg-brand text-brand-foreground",
          tone === "danger" && "bg-destructive text-destructive-foreground",
          className,
        )}
        {...props}
      >
        {digits}
        {label ? <span className="sr-only">, {label}</span> : null}
      </span>
    </Presence>
  );
}

export { badgeVariants };
